import { API, graphqlOperation } from 'aws-amplify';
import { 
  listUsers,
  getUser 
} from 'graphql/queries';
import { 
  createUser as createUserMutation,
  updateUser as updateUserMutation
} from 'graphql/mutations';
import * as logger from 'utils/logger';

// Pagination configuration
const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 200;
const FETCH_ALL_BATCH_SIZE = 100;

/**
 * Fetches a single page of users for a given client
 * @param {string} clientId - The client identifier to filter users
 * @param {string|null} nextToken - Pagination token for next page
 * @param {number} pageSize - Number of items per page (max 200)
 * @returns {Promise<{items: Array, nextToken: string|null, hasMore: boolean}>} Paginated response
 * @throws {Error} If the GraphQL query fails
 */
export const fetchUsersPage = async (clientId, nextToken = null, pageSize = DEFAULT_PAGE_SIZE) => {
  // Parameter validation
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }
  if (nextToken !== null && typeof nextToken !== 'string') {
    throw new Error('nextToken must be a string or null');
  }
  if (typeof pageSize !== 'number' || pageSize < 1) {
    throw new Error('pageSize must be a positive number');
  }

  try {
    const filter = {
      client: { eq: clientId }
    };
    
    // Ensure pageSize doesn't exceed maximum
    const limit = Math.min(pageSize, MAX_PAGE_SIZE);
    
    const params = {
      filter,
      limit
    };
    
    if (nextToken) {
      params.nextToken = nextToken;
    }
    
    const result = await API.graphql(
      graphqlOperation(listUsers, params)
    );
    
    return {
      items: result.data.listUsers.items || [],
      nextToken: result.data.listUsers.nextToken,
      hasMore: !!result.data.listUsers.nextToken
    };
  } catch (error) {
    logger.error('Failed to fetch users page', { clientId, error });
    throw new Error('Unable to fetch users. Please try again.');
  }
};

/**
 * Fetches all users for a client using pagination
 * @param {string} clientId - The client identifier to filter users
 * @returns {Promise<Array>} Array of all user records
 * @throws {Error} If the GraphQL query fails
 */
export const fetchUsers = async (clientId) => {
  // Parameter validation
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    const allItems = [];
    let nextToken = null;
    let fetchCount = 0;
    const maxFetches = 50; // Safety limit to prevent infinite loops
    
    do {
      const response = await fetchUsersPage(clientId, nextToken, FETCH_ALL_BATCH_SIZE);
      allItems.push(...response.items);
      nextToken = response.nextToken;
      fetchCount++;
      
      // Safety check to prevent infinite loops
      if (fetchCount >= maxFetches) {
        logger.warn('Reached maximum fetch limit while fetching users', { 
          maxFetches, 
          fetchCount, 
          itemsFetched: allItems.length 
        });
        break;
      }
    } while (nextToken);
    
    logger.debug('Successfully fetched all users', { 
      totalItems: allItems.length, 
      batchCount: fetchCount 
    });
    return allItems;
  } catch (error) {
    logger.error('Failed to fetch all users', { clientId, error });
    throw new Error('Unable to fetch users. Please try again.');
  }
};

/**
 * Fetches a single user by their ID
 * @param {string} id - The user ID
 * @returns {Promise<Object>} User record
 * @throws {Error} If the GraphQL query fails
 */
export const fetchUser = async (id) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid user ID is required');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(getUser, { id })
    );
    
    return result.data.getUser;
  } catch (error) {
    logger.error('Failed to fetch user', { id, error });
    throw new Error('Unable to fetch user details. Please try again.');
  }
};

/**
 * Creates a new user record
 * @param {Object} userData - User data including userName, email, client, etc.
 * @returns {Promise<Object>} Created user record
 * @throws {Error} If the GraphQL mutation fails
 */
export const createUser = async (userData) => {
  // Parameter validation
  if (!userData || typeof userData !== 'object') {
    throw new Error('User data is required');
  }
  if (!userData.userName || typeof userData.userName !== 'string') {
    throw new Error('Valid userName is required');
  }
  if (!userData.email || typeof userData.email !== 'string') {
    throw new Error('Valid email is required');
  }
  if (!userData.client || typeof userData.client !== 'string') {
    throw new Error('Valid client is required');
  }

  try {
    const input = {
      ...userData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(createUserMutation, { input })
    );
    
    return result.data.createUser;
  } catch (error) {
    logger.error('Failed to create user', { 
      userName: userData.userName, 
      client: userData.client, 
      error 
    });
    throw new Error('Unable to create user. Please try again.');
  }
};

/**
 * Updates an existing user record
 * @param {string} id - The user ID to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated user record
 * @throws {Error} If the GraphQL mutation fails
 */
export const updateUser = async (id, updates) => {
  // Parameter validation
  if (!id || typeof id !== 'string') {
    throw new Error('Valid user ID is required');
  }
  if (!updates || typeof updates !== 'object') {
    throw new Error('Updates object is required');
  }

  try {
    const input = {
      id,
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(updateUserMutation, { input })
    );
    
    return result.data.updateUser;
  } catch (error) {
    logger.error('Failed to update user', { id, error });
    throw new Error('Unable to update user. Please try again.');
  }
};

/**
 * Links a user account to an employee record
 * @param {string} userId - The user ID
 * @param {string} employeeId - The employee ID to link
 * @returns {Promise<Object>} Updated user record with employeeId
 * @throws {Error} If the GraphQL mutation fails
 */
export const linkUserToEmployee = async (userId, employeeId) => {
  // Parameter validation
  if (!userId || typeof userId !== 'string') {
    throw new Error('Valid user ID is required');
  }
  if (!employeeId || typeof employeeId !== 'string') {
    throw new Error('Valid employee ID is required');
  }

  try {
    const input = {
      id: userId,
      employeeId,
      updatedAt: new Date().toISOString()
    };
    
    const result = await API.graphql(
      graphqlOperation(updateUserMutation, { input })
    );
    
    return result.data.updateUser;
  } catch (error) {
    logger.error('Failed to link user to employee', { userId, employeeId, error });
    throw new Error('Unable to link user to employee. Please try again.');
  }
};

/**
 * Unlinks a user account from their employee record
 * @param {string} userId - The user ID to unlink
 * @returns {Promise<Object>} Updated user record with employeeId=null
 * @throws {Error} If the GraphQL mutation fails
 */
export const unlinkUserFromEmployee = async (userId) => {
  // Parameter validation
  if (!userId || typeof userId !== 'string') {
    throw new Error('Valid user ID is required');
  }

  try {
    // Try using the exact GraphQL query format
    const query = /* GraphQL */ `
      mutation UpdateUser($input: UpdateUserInput!) {
        updateUser(input: $input) {
          id
          client
          userName
          firstName
          lastName
          email
          role
          employeeId
          createdAt
          updatedAt
          __typename
        }
      }
    `;
    
    const input = {
      id: userId,
      employeeId: null  // Set to null to clear the relationship
      // Removed updatedAt - User model doesn't have this field
    };
    
    // Log the input to verify null is being sent correctly
    logger.info('Unlinking user from employee with input:', { input });
    
    const result = await API.graphql({
      query,
      variables: { input }
    });
    
    // Log the result to see what's returned
    logger.info('User unlink result:', { result: result.data.updateUser });
    
    return result.data.updateUser;
  } catch (error) {
    logger.error('Failed to unlink user from employee', { userId, error });
    throw new Error('Unable to unlink user from employee. Please try again.');
  }
};

/**
 * Checks if a username is unique across the system
 * @param {string} userName - The username to check
 * @param {string|null} excludeId - User ID to exclude from check (for updates)
 * @returns {Promise<boolean>} True if username is unique, false otherwise
 */
export const checkUsernameUniqueness = async (userName, excludeId = null) => {
  // Parameter validation
  if (!userName || typeof userName !== 'string') {
    throw new Error('Valid userName is required');
  }
  if (excludeId !== null && typeof excludeId !== 'string') {
    throw new Error('excludeId must be a string or null');
  }

  try {
    const filter = {
      userName: { eq: userName }
    };
    
    const result = await API.graphql(
      graphqlOperation(listUsers, { 
        filter,
        limit: 1
      })
    );
    
    const items = result.data.listUsers.items;
    if (excludeId) {
      return items.length === 0 || (items.length === 1 && items[0].id === excludeId);
    }
    
    return items.length === 0;
  } catch (error) {
    logger.error('Failed to check username uniqueness', { userName, error });
    return false;
  }
};